import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { sendEmail } from '../utils/emailService';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      const result = await sendEmail(formData);
      
      if (result.success) {
        setSubmitStatus({
          type: 'success',
          message: 'Thank you! Your message has been sent successfully. We\'ll get back to you soon!'
        });
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus({
          type: 'error',
          message: result.message || 'Failed to send message. Please try again.'
        });
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'An unexpected error occurred. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const socialLinks = [
    { name: 'GitHub', href: 'https://github.com/harrytrivedi', icon: '🐙' },
    { name: 'LinkedIn', href: 'https://www.linkedin.com/in/harsh-trivedi-a512a5229/', icon: '💼' },
    { name: 'Instagram', href: 'https://www.instagram.com/harrydevelops/', icon: '📸' },
    { name: 'Email', href: 'mailto:developer.trifusion@gmail.com', icon: '✉️' },
  ];

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-indigo-800 via-gray-800 to-purple-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Get in <span className="bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">Touch</span>
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-purple-300 to-indigo-300 mx-auto rounded-full"></div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <h3 className="text-2xl font-semibold text-white">
              Let's Connect
            </h3>
            <p className="text-gray-200 leading-relaxed">
              I am always open to new opportunities and collaborations. Feel free to reach out
              to discuss projects, ideas, or potential partnerships with Trifusion Solutions.
              Let's connect and explore how we can create innovative technology solutions together.
            </p>

            <div className="flex space-x-4">
              {socialLinks.map((link, index) => (
                <motion.a
                  key={index}
                  href={link.href}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="p-3 bg-gradient-to-br from-gray-700/50 to-purple-700/30 rounded-full shadow-md border border-purple-400/20 hover:shadow-lg hover:shadow-purple-400/10 transition-all duration-300 backdrop-blur-sm"
                >
                  <span className="text-xl">{link.icon}</span>
                  <span className="sr-only">{link.name}</span>
                </motion.a>
              ))}
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            onSubmit={handleSubmit}
            className="space-y-6 bg-gradient-to-br from-gray-700/50 to-purple-700/30 p-8 rounded-2xl shadow-lg border border-purple-400/20 backdrop-blur-sm"
          >
            <div>
              <label htmlFor="name" className="block text-white font-medium mb-2">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-purple-400/30 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-gray-600/30 text-white placeholder-gray-300 transition-all duration-300 backdrop-blur-sm"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-white font-medium mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-purple-400/30 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-gray-600/30 text-white placeholder-gray-300 transition-all duration-300 backdrop-blur-sm"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-white font-medium mb-2">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={4}
                className="w-full px-4 py-3 border border-purple-400/30 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-gray-600/30 text-white placeholder-gray-300 transition-all duration-300 backdrop-blur-sm"
              ></textarea>
            </div>

            {/* Status Message */}
            {submitStatus.type && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-xl border ${
                  submitStatus.type === 'success'
                    ? 'bg-green-500/10 border-green-400/30 text-green-300'
                    : 'bg-red-500/10 border-red-400/30 text-red-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-lg">
                    {submitStatus.type === 'success' ? '✅' : '❌'}
                  </span>
                  <span className="font-medium">{submitStatus.message}</span>
                </div>
              </motion.div>
            )}

            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              className={`w-full font-semibold py-4 rounded-xl transition-all duration-300 shadow-lg transform ${
                isSubmitting
                  ? 'bg-gray-600 cursor-not-allowed opacity-70'
                  : 'bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-400 hover:to-indigo-400 text-white hover:shadow-purple-400/25 hover:scale-[1.02]'
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Sending...</span>
                </div>
              ) : (
                'Send Message'
              )}
            </motion.button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}